/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React from "react";

import { Box, Button, Divider, Fade, IconButton, Modal, Stack, Tooltip, Typography, useTheme } from "@mui/material";

import Backdrop from "@mui/material/Backdrop";
import { ModalBase, TechwiseNotification } from "../../utils/Interfaces";
import { selectNotifications } from "./notificationsSlice";
import { Close, NotificationsOff } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { customScrollbarStyle } from "../../utils/Functions";
import CancelIcon from "@mui/icons-material/Cancel";
import { dismissAllNotifications, dismissNotification } from "./notificationsFunctions";

function NotificationsModal(props: ModalBase) {
    const { open, handleClose } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const theme = useTheme();

    const notifications: TechwiseNotification[] = useAppSelector(selectNotifications);

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={handleClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            minWidth: 600,
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h5" sx={{ mb: 3 }}>
                                Notifications
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start">
                                <IconButton sx={{ mt: -1, mr: -3 }} onClick={handleClose}>
                                    <Close fontSize="large" />
                                </IconButton>
                            </Stack>
                        </Stack>

                        {notifications.length > 2 && (
                            <Button
                                size="small"
                                variant="contained"
                                sx={{ p: 1, mb: 3 }}
                                onClick={() => dismissAllNotifications(dispatch)}
                            >
                                <Stack direction="row" justifyContent="flex-start">
                                    <NotificationsOff sx={{ ml: 0, mr: 0, mt: 0, mb: 0 }} />
                                    <Typography sx={{ ml: 2, mr: 0, mt: 0, mb: 0 }}>Dismiss All</Typography>
                                </Stack>
                            </Button>
                        )}

                        {notifications.length === 0 && <Typography variant="subtitle2">No notifications available.</Typography>}

                        {notifications.length > 0 && (
                            <Box
                                sx={{
                                    maxHeight: "calc(100vh - 150px)",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    ...customScrollbarStyle(),
                                }}
                            >
                                {notifications.map((notification: TechwiseNotification, index: number) => {
                                    return (
                                        <Box key={index}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography fontWeight="bold" sx={{ mb: 1 }}>
                                                    {notification.title}
                                                </Typography>
                                                <Tooltip title="Dismiss notification.">
                                                    <IconButton onClick={() => dismissNotification(notification.id, dispatch)}>
                                                        <CancelIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                            <Typography variant="subtitle1" component="pre">
                                                {notification.description}
                                            </Typography>

                                            {index !== notifications.length - 1 && <Divider sx={{ mt: 2, mb: 2 }} />}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
}

export default NotificationsModal;
